/**
 * PWAInstallPrompt — PWA 安装提示组件
 *
 * 功能：
 * - 拦截浏览器原生的 beforeinstallprompt 事件
 * - 显示自定义安装引导 UI
 * - 支持 iOS Safari 手动安装引导
 * - 记住用户拒绝状态（7 天内不再提示）
 * - 支持延迟显示（用户访问 30 秒后提示）
 *
 * 使用示例：
 * <PWAInstallPrompt />
 */

import React, { useState, useEffect, useCallback, memo } from 'react';
import { Download, X, Share, ArrowUp } from 'lucide-react';

// ★ 常量配置
const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 天
const PROMPT_DELAY = 30 * 1000;                     // 30 秒后提示
const PROMPT_SCROLL_THRESHOLD = 30;                  // 滚动 30% 后提示

/**
 * 检测是否为 iOS Safari
 */
function isIOSSafari() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
  return isIOS && isSafari;
}

/**
 * 检测是否已安装（standalone 模式）
 */
function isInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

/**
 * 检测是否已拒绝过安装提示
 */
function wasDismissed() {
  try {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (!dismissedAt) return false;
    return Date.now() - parseInt(dismissedAt, 10) < DISMISS_DURATION;
  } catch {
    return false;
  }
}

/**
 * PWA 安装提示主组件
 */
const PWAInstallPrompt = memo(() => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

  // ★ 拦截安装事件
  useEffect(() => {
    if (isInstalled()) return;
    if (wasDismissed()) return;

    const handleInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // 检测 iOS
    const ios = isIOSSafari();
    setIsIOS(ios);

    // 延迟显示（提高用户接受率）
    const delayTimer = setTimeout(() => {
      if (ios) {
        setShowBanner(true);
      }
    }, PROMPT_DELAY);

    // 滚动触发
    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > PROMPT_SCROLL_THRESHOLD && !wasDismissed() && !isInstalled()) {
        setShowBanner(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('beforeinstallprompt', handleInstallPrompt);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(delayTimer);
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // ★ 触发安装
  const handleInstall = useCallback(async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }

    if (!deferredPrompt) return;

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('[PWA] User accepted install prompt');
      } else {
        // 用户拒绝，记录状态
        try {
          localStorage.setItem(DISMISS_KEY, Date.now().toString());
        } catch {}
      }

      setDeferredPrompt(null);
      setShowBanner(false);
      setIsInstallable(false);
    } catch (err) {
      console.warn('[PWA] Install failed:', err);
    }
  }, [deferredPrompt, isIOS]);

  // ★ 关闭提示
  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    setShowIOSGuide(false);
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch {}
  }, []);

  // 不显示的情况
  if (!showBanner || isInstalled()) return null;

  return (
    <>
      {/* ★ 底部安装横幅 */}
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 animate-slide-up"
        role="dialog"
        aria-label="安装应用提示"
      >
        <div className="bg-gradient-to-r from-slate-900/95 to-slate-800/95 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-4 shadow-2xl shadow-black/20">
          {/* 关闭按钮 */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-slate-400 hover:text-white transition-colors p-1 rounded-full hover:bg-slate-700/50"
            aria-label="关闭安装提示"
          >
            <X className="w-4 h-4" />
          </button>

          {/* 内容 */}
          <div className="flex items-center gap-3 pr-6">
            {/* App 图标 */}
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <span className="text-xl">🐱</span>
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-white">安装西蒙的猫</h3>
              <p className="text-xs text-slate-400 mt-0.5">添加到主屏幕，享受原生应用体验</p>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleDismiss}
              className="flex-1 px-3 py-2 text-xs text-slate-400 hover:text-white rounded-lg hover:bg-slate-700/50 transition-colors"
            >
              稍后再说
            </button>
            <button
              onClick={handleInstall}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              立即安装
            </button>
          </div>
        </div>
      </div>

      {/* ★ iOS Safari 安装引导弹窗 */}
      {showIOSGuide && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleDismiss}
          role="dialog"
          aria-label="iOS 安装指南"
        >
          <div
            className="bg-slate-900 rounded-2xl p-6 max-w-sm w-full border border-slate-700/50"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-white mb-4">安装到主屏幕</h3>

            <div className="space-y-4 text-sm text-slate-300">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                <p>点击底部工具栏的 <Share className="w-4 h-4 inline mx-1" /> 分享按钮</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                <p>向上滑动找到 <ArrowUp className="w-4 h-4 inline mx-1" /> 添加到主屏幕</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                <p>点击「添加」完成安装</p>
              </div>
            </div>

            <button
              onClick={handleDismiss}
              className="w-full mt-6 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors"
            >
              明白了
            </button>
          </div>
        </div>
      )}
    </>
  );
});
PWAInstallPrompt.displayName = 'PWAInstallPrompt';

/**
 * 应用更新提示
 */
export const PWAUpdatePrompt = memo(() => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // 监听 SW 更新
    const handleUpdate = () => {
      if (window.__SW_READY__) {
        // 从 vite-plugin-pwa 注册的 SW
        navigator.serviceWorker.getRegistration().then((reg) => {
          if (reg?.waiting) {
            setWaitingWorker(reg.waiting);
            setShowUpdate(true);
          }
        });
      }
    };

    window.addEventListener('sw-updated', handleUpdate);

    return () => {
      window.removeEventListener('sw-updated', handleUpdate);
    };
  }, []);

  const handleUpdate = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
    setShowUpdate(false);
  }, [waitingWorker]);

  if (!showUpdate) return null;

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-down">
      <div className="bg-slate-900/95 backdrop-blur-xl rounded-xl border border-slate-700/50 p-4 shadow-2xl max-w-xs">
        <p className="text-sm text-white mb-3">有新版本可用！</p>
        <div className="flex gap-2">
          <button
            onClick={() => setShowUpdate(false)}
            className="flex-1 px-3 py-1.5 text-xs text-slate-400 hover:text-white rounded-lg hover:bg-slate-700/50 transition-colors"
          >
            稍后
          </button>
          <button
            onClick={handleUpdate}
            className="flex-1 px-3 py-1.5 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
          >
            更新
          </button>
        </div>
      </div>
    </div>
  );
});
PWAUpdatePrompt.displayName = 'PWAUpdatePrompt';

export default PWAInstallPrompt;
